import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { CloudUpload, PhotoCamera } from '@mui/icons-material';
// import { useTranslation } from 'react-i18next'; // Not used in this component

interface PhotoUploadStepProps {
  onPhotoUpload: (file: File, petType: 'dog' | 'cat') => void;
  onSkipPhoto: (petType: 'dog' | 'cat') => void;
  onCancel: () => void;
  isLoading: boolean;
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr';
}

export const PhotoUploadStep: React.FC<PhotoUploadStepProps> = ({
  onPhotoUpload,
  onSkipPhoto,
  onCancel,
  isLoading,
  userLanguage
}) => {
  // Removed unused useTranslation hook
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [petType, setPetType] = useState<'dog' | 'cat'>('dog');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Language-specific content
  const getContent = () => {
    const content = {
      en: {
        title: "Let's Start with a Photo 📸",
        description: "Upload a clear photo of your pet to personalize their profile.",
        petTypeLabel: "What type of pet do you have?",
        uploadButton: "Choose Photo",
        analyzeButton: "Upload & Continue",
        skipButton: "Skip Photo Upload",
        cancelButton: "Cancel",
        dragDropText: "Drag and drop a photo here, or click to select",
        fileRequirements: "Supported formats: JPEG, PNG, WebP (max 10MB)",
        selectPetTypeError: "Please select your pet type",
        noFileError: "Please select a photo to upload"
      },
      'zh-CN': {
        title: "让我们从一张照片开始 📸",
        description: "上传您宠物的清晰照片以个性化他们的资料。",
        petTypeLabel: "您有什么类型的宠物？",
        uploadButton: "选择照片",
        analyzeButton: "上传并继续",
        skipButton: "跳过照片上传",
        cancelButton: "取消",
        dragDropText: "拖拽照片到这里，或点击选择",
        fileRequirements: "支持格式：JPEG、PNG、WebP（最大10MB）",
        selectPetTypeError: "请选择您的宠物类型",
        noFileError: "请选择要上传的照片"
      },
      es: {
        title: "Empecemos con una Foto 📸",
        description: "Sube una foto clara de tu mascota para personalizar su perfil.",
        petTypeLabel: "¿Qué tipo de mascota tienes?",
        uploadButton: "Elegir Foto",
        analyzeButton: "Subir y Continuar",
        skipButton: "Omitir Subida de Foto",
        cancelButton: "Cancelar",
        dragDropText: "Arrastra y suelta una foto aquí, o haz clic para seleccionar",
        fileRequirements: "Formatos admitidos: JPEG, PNG, WebP (máx 10MB)",
        selectPetTypeError: "Por favor selecciona el tipo de tu mascota",
        noFileError: "Por favor selecciona una foto para subir"
      },
      fr: {
        title: "Commençons par une Photo 📸",
        description: "Téléchargez une photo claire de votre animal pour personnaliser son profil.",
        petTypeLabel: "Quel type d'animal avez-vous ?",
        uploadButton: "Choisir Photo",
        analyzeButton: "Télécharger et Continuer",
        skipButton: "Ignorer Téléchargement",
        cancelButton: "Annuler",
        dragDropText: "Glissez-déposez une photo ici, ou cliquez pour sélectionner",
        fileRequirements: "Formats pris en charge : JPEG, PNG, WebP (max 10MB)",
        selectPetTypeError: "Veuillez sélectionner le type de votre animal",
        noFileError: "Veuillez sélectionner une photo à télécharger"
      }
    };

    return content[userLanguage] || content.en;
  };

  const content = getContent();

  const handleFileSelect = useCallback((file: File) => {
    // Validate file
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(content.fileRequirements);
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      setError(content.fileRequirements);
      return;
    }

    setSelectedFile(file);
    setError(null);

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }, [content]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleAnalyze = useCallback(() => {
    if (!selectedFile) {
      setError(content.noFileError);
      return;
    }

    if (!petType) {
      setError(content.selectPetTypeError);
      return;
    }

    setError(null);
    onPhotoUpload(selectedFile, petType);
  }, [selectedFile, petType, onPhotoUpload, content]);

  const handleSkip = useCallback(() => {
    if (!petType) {
      setError(content.selectPetTypeError);
      return;
    }

    setError(null);
    onSkipPhoto(petType);
  }, [petType, onSkipPhoto, content]);

  // Cleanup preview URL
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <Box>
      {/* Header */}
      <Box textAlign="center" mb={isMobile ? 3 : 4}>
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h1" 
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          {content.title}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ px: isMobile ? 2 : 0, fontSize: isMobile ? '0.9rem' : '1rem' }}
        >
          {content.description}
        </Typography>
      </Box>

      {/* Pet Type Selection */}
      <Box mb={isMobile ? 3 : 4}>
        <FormControl component="fieldset">
          <FormLabel 
            component="legend" 
            sx={{ 
              mb: 2, 
              fontSize: isMobile ? '1rem' : '1.1rem',
              textAlign: 'center',
              fontWeight: 600
            }}
          >
            {content.petTypeLabel}
          </FormLabel>
          <RadioGroup
            row={!isMobile}
            value={petType}
            onChange={(e) => setPetType(e.target.value as 'dog' | 'cat')}
            sx={{ 
              justifyContent: 'center',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 1 : 0
            }}
          >
            <FormControlLabel
              value="dog"
              control={<Radio />}
              label={userLanguage === 'zh-CN' ? '狗' : userLanguage === 'es' ? 'Perro' : userLanguage === 'fr' ? 'Chien' : 'Dog'}
              sx={{ 
                minHeight: isMobile ? 44 : 'auto',
                '& .MuiFormControlLabel-label': {
                  fontSize: isMobile ? '1rem' : '0.875rem'
                }
              }}
            />
            <FormControlLabel
              value="cat"
              control={<Radio />}
              label={userLanguage === 'zh-CN' ? '猫' : userLanguage === 'es' ? 'Gato' : userLanguage === 'fr' ? 'Chat' : 'Cat'}
              sx={{ 
                minHeight: isMobile ? 44 : 'auto',
                '& .MuiFormControlLabel-label': {
                  fontSize: isMobile ? '1rem' : '0.875rem'
                }
              }}
            />
          </RadioGroup>
        </FormControl>
      </Box>

      {/* File Upload Area */}
      <Card
        variant="outlined"
        sx={{
          mb: isMobile ? 2 : 3,
          border: '2px dashed',
          borderColor: 'primary.main',
          backgroundColor: 'grey.50'
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: isMobile ? 3 : 4 }}>
          {previewUrl ? (
            // Preview mode
            <Box>
              <Box
                component="img"
                src={previewUrl}
                alt="Pet preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: isMobile ? 200 : 300,
                  borderRadius: 2,
                  mb: 2,
                  boxShadow: 2
                }}
              />
              <Typography 
                variant="body2" 
                color="success.main" 
                gutterBottom
                sx={{ fontSize: isMobile ? '0.85rem' : '0.875rem' }}
              >
                ✓ {selectedFile?.name}
              </Typography>
              <Button
                variant="outlined"
                size={isMobile ? "medium" : "large"}
                onClick={() => {
                  setSelectedFile(null);
                  setPreviewUrl(null);
                }}
                sx={{ minHeight: isMobile ? 44 : 'auto' }}
              >
                {userLanguage === 'zh-CN' ? '更换照片' : userLanguage === 'es' ? 'Cambiar Foto' : userLanguage === 'fr' ? 'Changer Photo' : 'Change Photo'}
              </Button>
            </Box>
          ) : (
            // Upload mode
            <Box
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{ cursor: 'pointer' }}
            >
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileInput}
                style={{ display: 'none' }}
                id="pet-photo-input"
              />
              <label htmlFor="pet-photo-input">
                <Box sx={{ cursor: 'pointer' }}>
                  <PhotoCamera sx={{ 
                    fontSize: isMobile ? 48 : 64, 
                    color: 'primary.main', 
                    mb: 2 
                  }} />
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {content.uploadButton}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: isMobile ? '0.85rem' : '0.875rem' }}
                  >
                    {content.dragDropText}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      mt: 1, 
                      display: 'block',
                      fontSize: isMobile ? '0.75rem' : '0.8rem'
                    }}
                  >
                    {content.fileRequirements}
                  </Typography>
                </Box>
              </label>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: isMobile ? 2 : 3 }}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box 
        display="flex" 
        justifyContent="center" 
        gap={isMobile ? 1.5 : 2} 
        flexWrap="wrap"
        flexDirection={isMobile ? 'column' : 'row'}
        sx={{ px: isMobile ? 1 : 0 }}
      >
        {selectedFile && (
          <Button
            variant="contained"
            size={isMobile ? "medium" : "large"}
            onClick={handleAnalyze}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <CloudUpload />}
            fullWidth={isMobile}
            sx={{ 
              minWidth: isMobile ? '100%' : 200,
              minHeight: isMobile ? 44 : 'auto'
            }}
          >
            {isLoading ? (
              userLanguage === 'zh-CN' ? '上传中...' : userLanguage === 'es' ? 'Subiendo...' : userLanguage === 'fr' ? 'Téléchargement...' : 'Uploading...'
            ) : (
              content.analyzeButton
            )}
          </Button>
        )}
        <Button
          variant="outlined"
          size={isMobile ? "medium" : "large"}
          onClick={handleSkip}
          disabled={isLoading}
          fullWidth={isMobile}
          sx={{ 
            minWidth: isMobile ? '100%' : 200,
            minHeight: isMobile ? 44 : 'auto'
          }}
        >
          {content.skipButton}
        </Button>
        <Button
          variant="text"
          size={isMobile ? "medium" : "large"}
          onClick={onCancel}
          disabled={isLoading}
          fullWidth={isMobile}
          sx={{ 
            minWidth: isMobile ? '100%' : 120,
            minHeight: isMobile ? 44 : 'auto'
          }}
        >
          {content.cancelButton}
        </Button>
      </Box>
    </Box>
  );
};
